import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosInstance';
import Button from 'apollo-react/components/Button';
import Typography from '../common/StyledTypography';
import Grid from 'apollo-react/components/Grid';
import { Form, withFormik } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import {
  FormikTextField,
  FormikPasswordInput,
} from '../common/FormikComponent';
import Info from 'apollo-react-icons/Info';
import Tooltip from 'apollo-react/components/Tooltip';
import { loginSchema } from '../../validation/auth';
import AuthLayout from './AuthLayout';
import { useAuth } from '../../contexts/AuthContext';

const initialValues = {
  email: '',
  password: '',
};

const onFormSubmit = async (
  values,
  { setSubmitting, setFieldError, props }
) => {
  try {
    const resp = await axios.post('/api/auth/login', values);
    if (props.refreshAfterLogin) {
      await props.refreshAfterLogin();
    }
    if (props.navigate) {
      props.navigate(`/${resp.data.role}/home`);
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'Login failed';
    setFieldError('email', errorMessage);
  }
  setSubmitting(false);
};

const Login = (props) => {
  const { setFieldValue, content } = props;
  const { t, ready } = useTranslation();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    if (content) {
      Object.keys(content).forEach((key) => {
        // eslint-disable-next-line no-prototype-builtins
        if (initialValues.hasOwnProperty(key)) {
          setFieldValue(key, content[key]);
        }
      });
    }
    setLoading(false);
  }, [content, setFieldValue]);

  // Show loading indicator if translations are not ready
  // This solves the issue of the translation keys being shown instead of the translated text during the initial load of the page.
  // The reason for this bug is i18n uses i18next-http-backend which means translations are loaded asynchronously via HTTP
  return loading || !ready ? (
    <Typography variant="bodySmall" style={{ margin: '8px 0' }}>
      {'Loading...'}
    </Typography>
  ) : (
    <AuthLayout>
      <Grid
        item
        justifyContent="center"
        width="463px"
        position="relative"
        zIndex={1}
      >
        <Grid container justifyContent="left" className="signInTitleGrid">
          <div>
            <h2 className="signInTitle">Sign In</h2>
            <p className="signInDesc">
              Log in by entering your email address <br /> and password.
            </p>
          </div>
        </Grid>

        <Grid container className="formContainer" sx={{ width: '100%' }}>
          <Grid container justifyContent="center" sx={{ width: '100%' }}>
            <Grid size={12}>
              <Form noValidate>
                <FormikTextField
                  name="email"
                  type="email"
                  label={
                    <span className="formLabelWithIcon">
                      Email Address
                      <Tooltip title={t('login.emailTooltip')} placement="top">
                        <Info
                          fontSize="extraSmall"
                          style={{ color: '#595959', cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </span>
                  }
                  key="email"
                  autoFocus
                  fullWidth
                  onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData('text').trim();
                    props.setFieldValue('email', text);
                  }}
                />
                <FormikPasswordInput
                  name="password"
                  key="password"
                  label={
                    <span className="formLabelWithIcon">
                      Password
                      <Tooltip
                        title={t('login.passwordTooltip')}
                        placement="top"
                      >
                        <Info
                          fontSize="extraSmall"
                          style={{ color: '#595959', cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </span>
                  }
                  fullWidth
                  onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData('text').trim();
                    props.setFieldValue('password', text);
                  }}
                />
                <Grid
                  container
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Grid size="grow">
                    <Link to="/forgot-password" className="forgotPassword">
                      Forgot password?
                    </Link>
                  </Grid>
                </Grid>
                <Button variant="primary" className="loginButton" type="submit">
                  Login
                </Button>
              </Form>
            </Grid>
          </Grid>
          <Grid container textAlign="center">
            <a className="ssoLink" href="/sso">
              Sign in with SSO
            </a>
          </Grid>
        </Grid>
        <Grid container justifyContent="center">
          <p className="signUpText">
            Sign up as an Enduser?{' '}
            <a href="/customer/register" className="signUpLink">
              Sign up here
            </a>
          </p>
        </Grid>
      </Grid>
    </AuthLayout>
  );
};

// Create FormikLogin HOC with translation support
const createFormikLogin = (t) =>
  withFormik({
    validationSchema: toFormikValidationSchema(loginSchema(t)),
    mapPropsToValues: () => initialValues,
    handleSubmit: onFormSubmit,
    validateOnChange: false,
    validateOnBlur: false,
  })(Login);

export default function LoginWithNavigate(props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refreshAfterLogin } = useAuth();

  const FormikLogin = useMemo(() => createFormikLogin(t), [t]);

  return (
    <FormikLogin
      {...props}
      navigate={navigate}
      refreshAfterLogin={refreshAfterLogin}
    />
  );
}
